import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2, EyeOff, Eye } from "lucide-react";

/**
 * Single draggable gallery card used by AdminGallery.
 */
export default function SortableGalleryItem({ item, onEdit, onDelete, onToggleVisible }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`gallery-card-${item.id}`}
      className="break-inside-avoid mb-5 rounded-lg overflow-hidden border border-border/70 bg-card shadow-ambient group"
    >
      <div className="relative">
        <img
          src={item.url}
          alt=""
          className={`w-full h-auto block ${item.visible ? "" : "opacity-40 grayscale"}`}
          loading="lazy"
        />
        <button
          type="button"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          data-testid={`gallery-drag-${item.id}`}
          className="absolute top-2 left-2 rounded-md bg-tz-espresso/80 text-tz-cream p-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="h-4 w-4" strokeWidth={1.5} />
        </button>
        {!item.visible && (
          <div className="absolute top-2 right-2 rounded-md bg-tz-espresso/80 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-tz-cream">
            Hidden
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{item.category}</div>
        <div className="mt-1 text-sm text-foreground line-clamp-2">
          {item.caption?.hr || item.caption?.en || "—"}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(item)}
            data-testid={`gallery-edit-${item.id}`}
            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-border text-foreground/80 hover:bg-muted"
          >
            <Pencil className="h-3 w-3" strokeWidth={1.6} /> Edit
          </button>
          <button
            type="button"
            onClick={() => onToggleVisible(item)}
            data-testid={`gallery-toggle-${item.id}`}
            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-border text-foreground/80 hover:bg-muted"
          >
            {item.visible ? <><EyeOff className="h-3 w-3" strokeWidth={1.6} /> Hide</> : <><Eye className="h-3 w-3" strokeWidth={1.6} /> Show</>}
          </button>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => onDelete(item)}
            data-testid={`gallery-delete-${item.id}`}
            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </div>
  );
}
